function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { spacing4, spacing8, spacing16, spacing24, spacing32, spacing48, spacing64, spacing128 } from "@churchofjesuschrist/eden-style-constants";
const gaps = {
  0: "0",
  4: spacing4,
  8: spacing8,
  16: spacing16,
  24: spacing24,
  32: spacing32,
  48: spacing48,
  64: spacing64,
  128: spacing128
};
const grids = {
  "0/4": {
    first: {
      flex: "1",
      min: "100%"
    },
    last: {
      flex: "1",
      min: "100%"
    }
  },
  "1/4": {
    first: {
      flex: "1"
    },
    last: {
      flex: "3",
      min: "75%"
    }
  },
  "1/3": {
    first: {
      flex: "1"
    },
    last: {
      flex: "2",
      min: "66%"
    }
  },
  "2/3": {
    first: {
      flex: "2",
      min: "66%"
    },
    last: {
      flex: "1"
    }
  },
  "3/4": {
    first: {
      flex: "3",
      min: "75%"
    },
    last: {
      flex: "1"
    }
  },
  "1/2": {
    first: {
      flex: "1"
    },
    last: {
      flex: "1"
    }
  },
  "4/4": {
    first: {
      flex: "1",
      min: "100%"
    },
    last: {
      flex: "1",
      min: "100%"
    }
  }
};

const getRowGapSize = _ref => {
  let {
    rowGapSize,
    gapSize
  } = _ref;
  return gaps[rowGapSize] || gaps[gapSize];
};

const getColumnGapSize = _ref2 => {
  let {
    columnGapSize,
    gapSize
  } = _ref2;
  return gaps[columnGapSize] || gaps[gapSize];
};

const Container = styled.div.withConfig({
  componentId: "sc-lsxfdu-0"
})(["display:flex;flex-wrap:wrap;column-gap:", ";row-gap:", ";"], getColumnGapSize, getRowGapSize);
const startEndStyles = css(["display:flex;flex-direction:column;flex:0 0 100%;& > *{flex:1 0 auto;}"]);
const Start = styled.div.withConfig({
  componentId: "sc-lsxfdu-1"
})(["", ";", ";"], startEndStyles, _ref3 => {
  var _grids$ratio, _grids$ratio$first, _grids$ratio2, _grids$ratio2$first;

  let {
    ratio,
    min
  } = _ref3;
  return `
      @media (min-width: ${min}) {
        flex: ${(_grids$ratio = grids[ratio]) === null || _grids$ratio === void 0 ? void 0 : (_grids$ratio$first = _grids$ratio.first) === null || _grids$ratio$first === void 0 ? void 0 : _grids$ratio$first.flex};
        min-width: ${((_grids$ratio2 = grids[ratio]) === null || _grids$ratio2 === void 0 ? void 0 : (_grids$ratio2$first = _grids$ratio2.first) === null || _grids$ratio2$first === void 0 ? void 0 : _grids$ratio2$first.min) || min};
      }
  `;
});
const End = styled.div.withConfig({
  componentId: "sc-lsxfdu-2"
})(["", ";", ";"], startEndStyles, _ref4 => {
  var _grids$ratio3, _grids$ratio3$last, _grids$ratio4, _grids$ratio4$last;

  let {
    ratio,
    min
  } = _ref4;
  return `
      @media (min-width: ${min}) {
        flex: ${(_grids$ratio3 = grids[ratio]) === null || _grids$ratio3 === void 0 ? void 0 : (_grids$ratio3$last = _grids$ratio3.last) === null || _grids$ratio3$last === void 0 ? void 0 : _grids$ratio3$last.flex};
        min-width: ${((_grids$ratio4 = grids[ratio]) === null || _grids$ratio4 === void 0 ? void 0 : (_grids$ratio4$last = _grids$ratio4.last) === null || _grids$ratio4$last === void 0 ? void 0 : _grids$ratio4$last.min) || min};
      }
  `;
});

const Split = _ref5 => {
  let {
    ratio = "1/2",
    gapSize = "16",
    rowGapSize,
    columnGapSize,
    min = "9.375rem",
    children,
    ...props
  } = _ref5;
  const childrenArray = React.Children.toArray(children);
  const firstChild = childrenArray.shift();
  return /*#__PURE__*/React.createElement(Container, _extends({
    gapSize: gapSize,
    rowGapSize: rowGapSize,
    columnGapSize: columnGapSize
  }, props), /*#__PURE__*/React.createElement(Start, {
    ratio: ratio,
    min: min
  }, firstChild), /*#__PURE__*/React.createElement(End, {
    ratio: ratio,
    min: min
  }, childrenArray));
};

Split.propTypes = {
  /** Items to turn into columns */
  children: PropTypes.node.isRequired,

  /** Where to place the vertical divide */
  ratio: PropTypes.oneOf(Object.keys(grids)),

  /** A valid px, rem, or em unit that defines the minimum size of each split item before wrapping */
  min: PropTypes.string,

  /** Gutter space between columns and rows */
  gapSize: PropTypes.oneOf(Object.keys(gaps)),

  /** Overrides gapSize for column gutter space only */
  columnGapSize: PropTypes.oneOf(Object.keys(gaps)),

  /** Overrides gapSize for row gutter space only */
  rowGapSize: PropTypes.oneOf(Object.keys(gaps))
};
export default Split;